import Bugsnag from "@bugsnag/js";
import format from "date-fns/format";
import parseJSON from "date-fns/parseJSON";
import { isNumber, orderBy } from "lodash";
import camelCase from "lodash/camelCase";
import mapKeys from "lodash/mapKeys";
import numeral from "numeral";

export function truncateString(str: string, length: number) {
  if (str.length > length) {
    return `${str.substring(0, length - 3)}...`;
  }
  return str;
}

export function numberSignificance({
  num,
  significance,
  maxDecimalPlaces,
}: any) {
  num = numeral(num);

  // Zero/falsy values are just 0
  if (!num.value()) {
    return "0";
  }

  let integer = num.value().toString().split(".").shift();

  // Negative number
  if (integer[0] === "-") {
    integer = integer.slice(1);
  }

  // Remove leading zero (should only happen when number < 1)
  if (integer[0] === "0") {
    integer = integer.slice(1);
  }

  if (significance === undefined) {
    significance = 4;
  }

  let needs = significance - integer.length;
  let format = "0,0";

  if (maxDecimalPlaces !== undefined && needs > maxDecimalPlaces) {
    needs = maxDecimalPlaces;
  }

  if (needs > 0) {
    format += ".";
    while (needs) {
      format += "0";
      needs--;
    }
  }

  return num.format(format);
}

export const setItemToLocalStorage = (key: string, value: any) => {
  try {
    // TODO: session storage on safari not working hence added temparary added localstorage
    sessionStorage.setItem(key, JSON.stringify(value));
    return localStorage.setItem(key, JSON.stringify(value));
  } catch (error: any) {
    console.error("error in setting value to session storage:", error);
    Bugsnag.notify(error);
  }

  return false;
};

export const getItemFromLocalStorage = (key: string) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}");
  } catch (error) {
    console.error("error in getting value from session storage:", error);
  }

  return null;
};

export const convertCamelcaseObject = (attachment: any) =>
  mapKeys(attachment, (v, k) => camelCase(k));

export const getFileExtensionFromName = (fileName: string) =>
  fileName?.split(".").slice(0, -1).join(".");

export const formatDateString = (date: string, dateFormat?: string) => {
  return format(parseJSON(date), dateFormat || "dd MMM yyyy HH:mm");
};

export const stopPropogation: any = (callback: any) => (e: any) => {
  e.stopPropagation();
  callback?.();
};

export const getShortSecurity = function (security: string) {
  if (!security || !security.match) {
    return security;
  }

  const match = security
    .replace(/\s+/g, " ")
    .match(/([\d\-&_A-Z_/]+ [A-Z]{2}) EQUITY/i);
  if (match) {
    return match[1];
  } else {
    return security;
  }
};

export const percentageFormatter = (value: any) => {
  return value ? numeral(value).format("0.00%") : "--";
};

export const getNA = () => {
  return "N/A";
};

export const millionize = (num: number) => {
  return num ? numeral(num / 1e6).format("0,0.00") : getNA();
};

export const medianBy = (arr: any[], fn: any, ignore_below_zero?: boolean) => {
  const cleanArr = arr.filter(
    (item) =>
      fn(item) && isNumber(fn(item)) && (!ignore_below_zero || fn(item) > 0)
  );
  const sorted = orderBy(cleanArr, fn, ["asc"]);

  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? fn(sorted[mid])
    : (fn(sorted[mid - 1]) + fn(sorted[mid])) / 2;
};

export const meanBy = (arr: any[], fn: any, ignore_below_zero?: boolean) => {
  const cleanArr = arr.filter(
    (item) =>
      fn(item) && isNumber(fn(item)) && (!ignore_below_zero || fn(item) > 0)
  );

  const sorted = orderBy(cleanArr, fn, ["asc"]);
  return (
    sorted.reduce((a, b) => {
      if (isNumber(fn(b))) {
        return a + fn(b);
      }

      return a;
    }, 0) / sorted.length
  );
};

export const maxBy = (arr: any[], fn: any) => {
  const cleanArr = arr.filter((item) => fn(item) && isNumber(fn(item)));
  const sorted = orderBy(cleanArr, fn, ["desc"]);

  return fn(sorted[0]);
};

export const minBy = (arr: any[], fn: any, ignore_below_zero?: boolean) => {
  const cleanArr = arr.filter(
    (item) =>
      fn(item) && isNumber(fn(item)) && (!ignore_below_zero || fn(item) > 0)
  );
  const sorted = orderBy(cleanArr, fn, ["asc"]);

  return fn(sorted[0]);
};
